import React, { createContext, useContext, useState, } from 'react';
import { useLocation, Routes, Route } from 'react-router-dom';
import { ThemeProvider, CssBaseline, useMediaQuery, useTheme, Box, CircularProgress } from "@mui/material";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Theme from './components/Theme';
import Login from './pages/Auth/Login';
import Forgot from './pages/Auth/Forgot';
import Otp from './pages/Auth/Otp';
import Confirm from './pages/Auth/Confirm';
import Sidebar from './components/Sidebar';
import PrivateRoute from './routes/PrivateRoute';
import routeData from './routes/routeData';
import Unauthorized from './pages/Unauthorized';
import PageNotFound from './pages/PageNotFound';
import Footer from './components/Footer';
import {  WebSocketProvider } from './context/webSocketContext';

// Create Snackbar Context
const SnackbarContext = createContext();

export const useSnackbar = () => useContext(SnackbarContext);

function App() {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const toastPosition = isMobile ? { vertical: 'top', horizontal: 'center' } : { vertical: 'top', horizontal: 'right' };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
    autoHideDuration: 3000,
    action: undefined,
    key: 0,
  });

  const showSnackbar = (message, optionsOrSeverity = "info") => {
    let severity = "info";
    let autoHideDuration = 3000;
    let action;

    if (typeof optionsOrSeverity === "string") {
      severity = optionsOrSeverity;
    } else if (optionsOrSeverity && typeof optionsOrSeverity === "object") {
      severity = optionsOrSeverity.variant || "info";
      if (optionsOrSeverity.autoHideDuration) {
        autoHideDuration = optionsOrSeverity.autoHideDuration;
      }
      if (optionsOrSeverity.action) {
        action = optionsOrSeverity.action;
      }
    }

    setSnackbar({
      open: true,
      message,
      severity,
      autoHideDuration,
      action,
      key: Date.now(),
    });
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const noSidebarRoutes = ['/', '/forgot', '/otp', '/confirm'];
  const hideSidebar = noSidebarRoutes.includes(location.pathname);

  // Define background colors based on severity
  const getBackgroundColor = (severity) => {
    switch (severity) {
      case 'success':
        return 'rgba(46, 125, 50, 0.9)'; // Green for success
      case 'error':
        return 'rgba(211, 47, 47, 0.9)'; // Red for failed
      case 'warning':
        return 'rgba(237, 108, 2, 0.9)'; // Orange for delete (warning)
      case 'socket':
        return 'rgba(0, 0, 0, 1)';
      case 'info':
        return 'rgba(2, 136, 209, 0.9)'; // Blue for info
      default:
        return 'rgba(2, 136, 209, 0.9)'; // Blue for info
    }
  };

  return (
    <ThemeProvider theme={Theme}>
      <CssBaseline />
      <SnackbarContext.Provider value={{ showSnackbar }}>
        <WebSocketProvider>

          {/* Global styles */}
          <style>
            {`
              body {
                margin: 0;
                padding: 0;
                font-family: 'Poppins', sans-serif;
                background-color: #f4f6f8;
              }
            `}
          </style>

          {/* Conditional rendering based on route */}
          {hideSidebar ? (
            <React.Suspense fallback={
              <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress sx={{ color: '#7C0BCC' }} />
              </Box>
            }>
              <Routes>
                <Route path="/" element={<Login />} />
                <Route path="/forgot" element={<Forgot />} />
                <Route path="/otp" element={<Otp />} />
                <Route path="/confirm" element={<Confirm />} />
              </Routes>
            </React.Suspense>
          ) : (
            <>
              <Sidebar>
                <React.Suspense fallback={
                  <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                    <CircularProgress sx={{ color: '#7C0BCC' }} />
                  </Box>
                }>
                  <Routes>
                    {routeData.map(({ path, element, roles }) => (
                      <Route
                        key={path}
                        path={path}
                        element={
                          <PrivateRoute allowedRoles={roles}>
                            {element}
                          </PrivateRoute>
                        }
                      />
                    ))}
                    <Route path="/unauthorized" element={<Unauthorized />} />
                    <Route path="*" element={<PageNotFound />} />
                  </Routes>
                </React.Suspense>
              </Sidebar>
              {!isMobile && <Footer />}
            </>
          )}

          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={handleClose}
            anchorOrigin={toastPosition}
            sx={{
              '& .MuiPaper-root': {
                backgroundColor: getBackgroundColor(snackbar.severity),
                color: '#fff',
                borderRadius: '8px',
              }
            }}
          >
            <Alert
              onClose={handleClose}
              severity={snackbar.severity}
              sx={{
                width: "100%",
                backgroundColor: "transparent",
                color: "inherit",
                "& .MuiAlert-icon": { color: "inherit" },
              }}
              action={
                typeof snackbar.action === "function"
                  ? snackbar.action(snackbar.key)
                  : snackbar.action
              }
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </WebSocketProvider>
      </SnackbarContext.Provider>
    </ThemeProvider>
  );
}

export default App;